<template>
  <v-card flat height="100%">
    <v-card-title>
      <v-layout>
        <v-flex xs8 ma-2>Behandlungsorte</v-flex>
        <v-spacer></v-spacer>
        <v-flex xs2 ma-2>
          <v-btn block color="primary" @click="back" :disabled="edited.name!==''">Zurück</v-btn>
        </v-flex>
        <v-flex xs2 ma-2>
          <v-btn block color="primary" @click="forward" :disabled="edited.name!==''">Weiter</v-btn>
        </v-flex>
      </v-layout>
    </v-card-title>
    <v-card-text>
      <v-layout wrap>
        <v-flex xs12 md12 lg6 pa-1>
          <v-data-table
            :headers="headers"
            :items="behandlungsorte"
            :sort-by="['name']"
            :sort-desc="[false]"
            disable-pagination
            hide-default-footer
          >
            <template v-slot:item.status="{ item }">
              <v-icon v-if="item.hasValidationErrors" color="red">mdi-alert-circle</v-icon>
              <v-icon v-if="!item.hasValidationErrors" color="green">mdi-checkbox-marked-circle</v-icon>
            </template>
            <template v-slot:item.gueltig="{ item }">
              <v-chip>{{ gueltig(item) }}</v-chip>
            </template>
            <template v-slot:item.hauptBehandlungsort="{ item }">
              <div v-if="item.hauptBehandlungsort">ja</div>
            </template>
            <template v-slot:item.action="{ item }">
              <v-icon class="pa-1" @click="edit(item)">mdi-pencil</v-icon>
              <v-icon class="pa-1" @click="remove(item)" v-if="!item.lizenzaenderung">mdi-delete</v-icon>
            </template>
          </v-data-table>
        </v-flex>
        <v-flex xs12 md12 lg6 pa-1>
          <v-form ref="form" v-model="valid" lazy-validation>
            <h2>Allgemein</h2>
            <v-layout wrap>
              <div v-if="edited.lizenzaenderung" class="red--text text-justify">
                Eine BSNR darf nur geändert werden, wenn diese vorher falsch hinterlegt war. Wenn die Praxis eine neue
                BSNR bekommen hat, muss ein weiterer Behandlungsort angelegt werden ("Aktiv ab" nicht vergessen). Wenn
                die alte BSNR ungültig geworden ist, muss dieser Behandlungsort über "Aktiv bis" deaktiviert werden. (In
                der Regel zum Ende des Quartals)
              </div>
              <v-flex xs12 sm6 md8 pa-1>
                <v-text-field v-model="edited.name" label="Name" :rules="nameRules" :counter="255"></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md2 pa-1>
                <v-text-field
                  v-model="edited.kuerzel"
                  label="Kürzel"
                  :rules="kuerzelRules"
                  :counter="5"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md2 pa-1>
                <v-text-field v-model="edited.bsnr" label="BSNR" :rules="bsnrRules" :counter="9"></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md6 pa-1>
                <v-menu
                  v-model="aktivAb"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px"
                  :disabled="!aktivAbEnabled"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="edited.aktivAb"
                      readonly
                      label="Aktiv-Ab"
                      v-on="on"
                      :rules="aktivAbRules"
                      clearable
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="edited.aktivAb" type="month" @input="aktivAb = false"></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12 sm6 md6 pa-1>
                <v-menu
                  v-model="aktivBis"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px"
                  :disabled="!aktivBisEnabled"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="edited.aktivBis"
                      readonly
                      label="Aktiv-Bis"
                      v-on="on"
                      clearable
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="edited.aktivBis" @input="aktivBis = false"></v-date-picker>
                </v-menu>
              </v-flex>
            </v-layout>
            <v-layout wrap>
              <v-flex
                xs12
                sm6
                md4
                pa-1
                v-if="edited.inaktivChangeAllowed=== undefined || edited.inaktivChangeAllowed===true"
              >
                <v-checkbox
                  v-model="edited.inaktiv"
                  label="Inaktiv (nur für Konvertierung)"
                  :disabled="inaktivEnabled"
                ></v-checkbox>
              </v-flex>
              <v-flex xs12 sm6 md4 pa-1>
                <v-checkbox
                  v-model="edited.datenuebernahmeAusKonvertierung"
                  label="Datenübernahme aus Vorprogramm"
                ></v-checkbox>
              </v-flex>
              <v-flex xs12 sm6 md4 pa-1 v-if="!sonderbestellung">
                <v-checkbox v-model="edited.hauptBehandlungsort" label="Hauptbehandlungsort"></v-checkbox>
              </v-flex>
            </v-layout>
            <h2>
              Adresse
              <v-btn
                text
                color="blue"
                class="ma-2"
                @click="adresseDialog=true"
                v-if="behandlungsorte.length > 0"
              >Adresse übernehmen</v-btn>
            </h2>
            <v-layout wrap>
              <v-flex xs12 sm6 md8 pa-1>
                <v-text-field
                  v-model="edited.adresse.strasse"
                  label="Straße"
                  :rules="strasseRules"
                  :counter="64"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md4 pa-1>
                <v-text-field
                  v-model="edited.adresse.hausnummer"
                  label="Hausnummer"
                  :rules="hausnummerRules"
                  :counter="9"
                ></v-text-field>
              </v-flex>
            </v-layout>
            <v-layout wrap>
              <v-flex xs12 sm6 md8 pa-1>
                <v-text-field
                  v-model="edited.adresse.adressZusatz"
                  label="Adresszusatz"
                  :rules="zusatzRules"
                  :counter="64"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md4 pa-1>
                <v-text-field
                  v-model="edited.adresse.postfach"
                  label="Postfach"
                  :rules="postfachRules"
                  :counter="32"
                ></v-text-field>
              </v-flex>
            </v-layout>
            <v-layout wrap>
              <v-flex xs12 sm6 md4 pa-1>
                <v-text-field
                  v-model="edited.adresse.plz"
                  label="Postleitzahl"
                  :rules="plzRules"
                  :counter="5"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md4 pa-1>
                <v-text-field
                  v-model="edited.adresse.ort"
                  label="Ort"
                  :rules="ortRules"
                  :counter="64"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md4 pa-1>
                <v-text-field
                  v-model="edited.adresse.land"
                  label="Land"
                  :rules="landRules"
                  :counter="3"
                ></v-text-field>
              </v-flex>
            </v-layout>
            <h2>
              Kontaktdaten
              <v-btn
                text
                color="blue"
                class="ma-2"
                @click="kontaktDialog=true"
                v-if="behandlungsorte.length > 0"
              >Kontaktdaten übernehmen</v-btn>
            </h2>
            <v-layout wrap>
              <v-flex xs12 sm6 md4 pa-1>
                <v-text-field
                  v-model="edited.telefonNummer"
                  label="Telefonnummer"
                  :rules="telefonRules"
                  :counter="32"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md4 pa-1>
                <v-text-field
                  v-model="edited.telefax"
                  label="Telefax"
                  :rules="telefaxRules"
                  :counter="32"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md4 pa-1>
                <v-text-field
                  v-model="edited.krankenhausIk"
                  label="Krankenhaus-IK"
                  :rules="krankenhausIKRules"
                  :counter="9"
                ></v-text-field>
              </v-flex>
            </v-layout>
            <v-layout wrap>
              <v-flex xs12 sm6 md12 pa-1>
                <v-text-field
                  v-model="edited.primaereEmailAdresse.email"
                  label="Primäre Email Adresse"
                  :rules="emailRules"
                  :counter="255"
                ></v-text-field>
              </v-flex>
            </v-layout>
            <div v-if="edited.hauptBehandlungsort">
              <h2>Bankdaten</h2>
              <v-layout wrap>
                <v-flex xs12 sm6 md12 pa-1>
                  <v-text-field
                    v-model="edited.bankdaten.bankName"
                    label="Bankname"
                    :rules="banknameRules"
                    :counter="255"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4 pa-1>
                  <v-text-field
                    v-model="edited.bankdaten.iban"
                    label="IBAN"
                    :rules="ibanRules"
                    :counter="34"
                    id="iban"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4 pa-1>
                  <v-text-field
                    v-model="edited.bankdaten.bic"
                    label="BIC"
                    :rules="bicRules"
                    :counter="11"
                    id="bic"
                  ></v-text-field>
                </v-flex>
              </v-layout>
              <h2>Support</h2>
              <v-layout wrap>
                <v-flex xs12 sm6 md4 pa-1>
                  <v-checkbox v-model="edited.erweiterterSupport" label="Erweiterter Support"></v-checkbox>
                </v-flex>
                <v-flex xs12 sm6 md4 pa-1>
                  <v-text-field v-model="edited.homepage" label="Homepage"></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4 pa-1 v-if="!edited.lizenzaenderung">
                  <v-select
                    v-model="edited.bisherigesPraxisProgramm"
                    :items="programme"
                    label="Bisheriges Praxisprogramm"
                  ></v-select>
                </v-flex>
              </v-layout>
              <h2>Preis</h2>
              <v-layout wrap>
                <v-flex xs12 sm6 md4 pa-1>
                  <v-text-field
                    v-model="edited.verkaufsPreis"
                    label="Verkaufspreis HBSNR"
                    :rules="preisRules"
                  ></v-text-field>
                </v-flex>
              </v-layout>
            </div>
            <v-layout wrap>
              <v-spacer></v-spacer>
              <v-btn class="ma-2" color="error" @click="clear">Bearbeitung abbrechen</v-btn>
              <v-btn class="ma-2" color="primary" @click="save">Speichern</v-btn>
            </v-layout>
          </v-form>
        </v-flex>
      </v-layout>
    </v-card-text>
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Wollen Sie diesen Eintrag wirklich löschen?</v-card-title>
        <v-card-text>Das Löschen kann nicht wieder rückgängig gemacht werden</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="deleteDialog = false">Abbrechen</v-btn>
          <v-btn color="blue darken-1" text @click="deleteBehandlungsort">Löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="adresseDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Adresse von bestehendem Behandlungsort übernehmen</v-card-title>
        <v-card-text>
          <v-select
            v-model="adresseDialogSelected"
            :items="behandlungsorte.map(ort => ort.adresse)"
            :item-text="adresseText"
            label="Adresse auswählen"
            return-object
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="adresseDialog = false">Abbrechen</v-btn>
          <v-btn color="blue darken-1" text @click="addAdresse">Übernehmen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="kontaktDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Kontaktdaten von bestehendem Behandlungsort übernehmen</v-card-title>
        <v-card-text>
          <v-select
            v-model="kontaktSelected"
            :items="behandlungsorte.map(ort => {return {telefonNummer: ort.telefonNummer,telefax: ort.telefax, email: ort.primaereEmailAdresse.email}})"
            :item-text="kontaktText"
            label="Kontaktdaten auswählen"
            return-object
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="kontaktDialog = false">Abbrechen</v-btn>
          <v-btn color="blue darken-1" text @click="addKontaktdaten">Übernehmen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>
<style>
#iban,
#bic {
  text-transform: uppercase;
}
</style>
<script>
import { mapState } from "vuex";

function trimObjectValues(obj) {
  if (obj !== undefined && obj !== null) {
    Object.keys(obj).map((k) => {
      var value = obj[k];
      if (value !== undefined && value !== null && typeof value == "string") {
        obj[k] = value.trim();
      } else if (value !== undefined && value !== null) {
        trimObjectValues(value);
      }
    });
  }
}

function edited() {
  return {
    name: "",
    kuerzel: "",
    bsnr: "",
    adresse: {
      strasse: "",
      hausnummer: "",
      adressZusatz: "",
      postfach: "",
      plz: "",
      ort: "",
      land: "",
    },
    aktivAb: null,
    aktivBis: null,
    hauptBehandlungsort: false,
    telefonNummer: "",
    krankenhausIk: "",
    primaereEmailAdresse: {
      email: "",
    },
    bankdaten: {
      bankName: "",
      iban: "",
      bic: "",
    },
    datenuebernahmeAusKonvertierung: true,
    lizenzaenderung: false,
  };
}

export default {
  data() {
    return {
      valid: false,
      headers: [
        { text: "", value: "status", sortable: false },
        { text: "Name", value: "name" },
        { text: "Kürzel", value: "kuerzel" },
        { text: "BSNR", value: "bsnr" },
        { text: "Gültig", value: "gueltig" },
        { text: "HB", value: "hauptBehandlungsort" },
        { text: "Aktionen", value: "action", sortable: false },
      ],
      aktivAb: false,
      aktivAbEnabled: true,
      aktivBis: false,
      aktivBisEnabled: true,
      inaktivEnabled: true,
      edited: edited(),
      deleteDialog: false,
      adresseDialog: false,
      adresseDialogSelected: null,
      kontaktDialog: false,
      kontaktSelected: null,
      deleteItem: null,
      nameRules: [
        (v) => !!v || "Der Name darf nicht leer sein",
        (v) => v.length <= 255 || "Der Name muss kürzer als 255 Zeichen sein",
      ],
      aktivAbRules: [
        () =>
          this.license.lizenzaenderung &&
          !this.edited.hauptBehandlungsort &&
          this.edited.datenuebernahmeAusKonvertierung
            ? false
            : true,
        (v) =>
          (this.license.lizenzaenderung &&
          !this.edited.inaktiv &&
          !this.edited.lizenzaenderung &&
          !(
            this.license.lizenzaenderung &&
            !this.edited.hauptBehandlungsort &&
            this.edited.datenuebernahmeAusKonvertierung
          )
            ? !!v && v !== ""
            : true) || "Aktiv-Ab muss gefüllt werden",
      ],
      kuerzelRules: [
        (v) => !!v || "Das Kürzel darf nicht leer sein",
        (v) => v.length <= 5 || "Das Kürzel muss kürzer als 5 Zeichen sein",
      ],
      bsnrRules: [
        (v) =>
          v.length === 0
            ? true
            : v.length === 9 || "Die BSNR sollte 9 Ziffern lang sein",
        (v) =>
          v.length === 0
            ? true
            : new RegExp("^[0-9]+$").test(v) ||
              "Die BSNR darf nur Ziffern enthalten",
      ],
      strasseRules: [
        (v) => !!v || "Die Straße darf nicht leer sein",
        (v) => v.length <= 64 || "Die Straße muss kürzer als 64 Zeichen sein",
      ],
      hausnummerRules: [
        (v) => !!v || "Die Hausnummer darf nicht leer sein",
        (v) => v.length <= 9 || "Die Hausnummer muss kürzer als 9 Zeichen sein",
      ],
      zusatzRules: [
        (v) =>
          v === undefined || v === null
            ? true
            : v.length <= 64 ||
              "Der Adresszusatz muss kürzer als 64 Zeichen sein",
      ],
      postfachRules: [
        (v) =>
          v === undefined || v === null
            ? true
            : v.length <= 32 || "Das Postfach muss kürzer als 32 Zeichen sein",
      ],
      plzRules: [
        (v) =>
          (v.length >= 4 && v.length <= 5) ||
          "Die PLZ muss zwischen 4 und 5 Ziffern lang sein",
        (v) =>
          new RegExp("^[0-9]+$").test(v) ||
          "Die PLZ darf nur Ziffern enthalten",
      ],
      ortRules: [
        (v) => !!v || "Der Ort darf nicht leer sein",
        (v) => v.length <= 64 || "Der Ort muss kürzer als 64 Zeichen sein",
      ],
      landRules: [
        (v) =>
          v === null || v === undefined
            ? true
            : v.length <= 3 || "Das Land muss kürzer als 3 Zeichen sein",
      ],
      telefonRules: [
        (v) =>
          this.edited.inaktiv === true
            ? true
            : !!v || "Die Telefonnummer darf nicht leer sein",
        (v) =>
          v === undefined || v === null || v === ""
            ? true
            : (new RegExp("^[0-9/-\\s]+$").test(v) && v.trim().length != 0) ||
              "Die Telefonummer darf nur Ziffern, Leerzeichen und / oder - enthalten",
      ],
      telefaxRules: [
        (v) =>
          v === undefined || v === null
            ? true
            : (new RegExp("^[0-9/-\\s]+$").test(v) && v.trim().length != 0) ||
              "Die Telefaxnummer darf nur Ziffern, Leerzeichen und / oder - enthalten",
      ],
      krankenhausIKRules: [
        (v) =>
          v === undefined || v === null
            ? true
            : (v.length === 0 ? true : v.length === 9) ||
              "Die Krankenhaus-IK muss 9 Ziffern lang sein",
        (v) =>
          v === undefined || v === null
            ? true
            : (v.length === 0 ? true : new RegExp("^[0-9]+$").test(v)) ||
              "Die Krankenhaus-IK darf nur Ziffern enthalten",
      ],
      emailRules: [
        (v) =>
          v === null
            ? true
            : v.length <= 255 || "Die Email muss kürzer als 255 Zeichen sein",
        (v) =>
          v === null
            ? true
            : (v.length === 0 ? true : /.+@.+\..+/.test(v)) ||
              "Die Email ist ungültig",
      ],
      banknameRules: [
        (v) =>
          this.edited.hauptBehandlungsort === false
            ? false
            : (v === null ? false : !!v) || "Der Bankname darf nicht leer sein",
        (v) =>
          this.edited.hauptBehandlungsort === false
            ? false
            : (v === null ? false : v.length <= 128) ||
              "Der Bankname muss kürzer als 128 Zeichen sein",
      ],
      ibanRules: [
        (v) =>
          this.edited.hauptBehandlungsort === false
            ? false
            : (v === null ? false : !!v) || "Die IBAN darf nicht leer sein",
        (v) =>
          this.edited.hauptBehandlungsort === false
            ? false
            : (v === null ? false : v.length <= 34) ||
              "Die IBAN muss kürzer als 34 Zeichen sein",
      ],
      bicRules: [
        (v) =>
          this.edited.hauptBehandlungsort === false
            ? false
            : (v === null ? false : !!v) || "Die BIC darf nicht leer sein",
        (v) =>
          this.edited.hauptBehandlungsort === false
            ? false
            : (v === null ? false : v.length <= 11) ||
              "Die BIC darf nicht mehr als 11 Zeichen enthalten",
      ],
      preisRules: [
        (v) =>
          v === undefined || v === null
            ? true
            : (v.length === 0 ? true : !isNaN(v)) ||
              "Der Preis darf keine Leerzeichen oder Buchstaben entahlten",
      ],
    };
  },
  computed: {
    ...mapState({
      programme: (state) => state.programme,
      behandlungsorte: (state) =>
        state.current.bestellung.lizenz.behandlungsorte,
      process: (state) => state.current.process,
      license: (state) => state.current.bestellung.lizenz,
      savedPrice: (state) => state.current.prices.praxis,
      sonderbestellung: (state) => state.current.bestellung.sonderbestellung,
    }),
  },
  mounted: function () {
    if (
      this.$route.params.process != this.process ||
      this.license === undefined
    ) {
      this.$store.commit("startLoading");
      this.$socket.sendObj({
        eventID:
          "de.t2med.lizenzomat.eventapi.usecase.order.BestellungRequestEvent",
        processId: this.$route.params.process,
        taskId: this.$route.params.task,
      });
    }
    this.edited.verkaufsPreis = this.savedPrice;
    this.edited.datenuebernahmeAusKonvertierung = !this.license.lizenzaenderung;
  },
  watch: {
    "edited.hauptBehandlungsort": function (nV, oV) {
      if (oV === false && nV === true) {
        this.edited.verkaufsPreis = this.savedPrice;
      }
    },
    "edited.inaktiv": function (nV) {
      if (nV === true) {
        this.aktivAbEnabled = false;
        this.aktivBisEnabled = false;
      } else {
        this.aktivAbEnabled = true;
        this.aktivBisEnabled = true;
      }
    },
    "edited.aktivAb": function (nV) {
      if ((nV !== undefined && nV !== null && nV !== "")  || (this.edited.aktivBis !== undefined && this.edited.aktivBis !== null && this.edited.aktivBis !== "")) {
        this.inaktivEnabled = false;
      } else {
        this.inaktivEnabled = true;
      }
    },
    "edited.aktivBis": function (nV) {
      if ((nV !== undefined && nV !== null && nV !== "" )|| (this.edited.aktivAb !== undefined && this.edited.aktivAb !== null && this.edited.aktivAb !== "")) {
        this.inaktivEnabled = false;
      } else {
        this.inaktivEnabled = true;
      }
    },
    behandlungsorte: function (nV, oV) {
      if (nV !== undefined && nV.length >= oV.length) {
        this.clear();
      }
    },
    license: function (nV) {
      if (nV !== undefined) {
        this.edited.datenuebernahmeAusKonvertierung = !this.license
          .lizenzaenderung;
      }
    },
  },
  methods: {
    edit: function (ort) {
      const behandlungsort = JSON.parse(JSON.stringify(ort));
      if (behandlungsort.aktivAb !== null) {
        behandlungsort.aktivAb = behandlungsort.aktivAb.substr(0, 7);
      }
      this.edited = behandlungsort;
      this.$refs.form.resetValidation();
      this.$vuetify.goTo(0);
    },
    gueltig: function (behandlungsort) {
      if (behandlungsort.inaktiv) {
        return "inaktiv";
      } else {
        const monthDateFormat = { month: "2-digit", year: "numeric" };
        const dateFormat = {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        };
        if (
          behandlungsort.aktivAb === null &&
          behandlungsort.aktivBis === null
        ) {
          return "aktiv";
        } else if (
          behandlungsort.aktivAb !== null &&
          behandlungsort.aktivBis === null
        ) {
          return (
            new Date(behandlungsort.aktivAb).toLocaleDateString(
              undefined,
              monthDateFormat
            ) + " - n. a."
          );
        } else if (
          behandlungsort.aktivAb === null &&
          behandlungsort.aktivBis !== null
        ) {
          return (
            "n. a. - " +
            new Date(behandlungsort.aktivBis).toLocaleDateString(
              undefined,
              dateFormat
            )
          );
        } else {
          return (
            new Date(behandlungsort.aktivAb).toLocaleDateString(
              undefined,
              monthDateFormat
            ) +
            " - " +
            new Date(behandlungsort.aktivBis).toLocaleDateString(
              undefined,
              monthDateFormat
            )
          );
        }
      }
    },
    addAdresse: function () {
      this.edited.adresse = JSON.parse(
        JSON.stringify(this.adresseDialogSelected)
      );
      this.adresseDialogSelected = null;
      this.adresseDialog = false;
    },
    adresseText: function (ort) {
      return (
        (ort.strasse !== null ? ort.strasse : "") +
        " " +
        (ort.hausnummer !== null ? ort.hausnummer : "") +
        " " +
        (ort.adressZusatz !== null ? ort.adressZusatz : "") +
        " " +
        (ort.postfach !== null ? ort.postfach : "") +
        " " +
        (ort.plz !== null ? ort.plz : "") +
        " " +
        (ort.ort !== null ? ort.ort : "") +
        " " +
        (ort.land !== null ? ort.land : "")
      );
    },
    addKontaktdaten: function () {
      const kontaktdaten = JSON.parse(JSON.stringify(this.kontaktSelected));
      this.edited.telefonNummer = kontaktdaten.telefonNummer;
      this.edited.primaereEmailAdresse.email = kontaktdaten.email;
      this.edited.telefax = kontaktdaten.telefax;
      this.kontaktDialog = false;
      this.kontaktSelected = null;
    },
    kontaktText: function (kontakt) {
      return (
        kontakt.telefonNummer + " " + kontakt.telefax + " " + kontakt.email
      );
    },
    clear: function () {
      this.edited = edited();
      this.$refs.form.resetValidation();
      this.edited.verkaufsPreis = this.savedPrice;
      this.edited.datenuebernahmeAusKonvertierung = !this.license
        .lizenzaenderung;
    },
    save: function () {
      if (this.$refs.form.validate()) {
        const behandlungsort = this.edited;
        if (
          behandlungsort.aktivAb !== null &&
          (behandlungsort.aktivAb.match(/-/g) || []).length < 2
        ) {
          behandlungsort.aktivAb += "-01";
        }
        if (
          behandlungsort.bankdaten.iban !== undefined &&
          behandlungsort.bankdaten.iban !== null
        ) {
          behandlungsort.bankdaten.iban = behandlungsort.bankdaten.iban.toUpperCase();
        }
        this.$store.commit("startLoading");
        trimObjectValues(behandlungsort);
        this.$socket.sendObj({
          eventID:
            "de.t2med.lizenzomat.eventapi.usecase.order.behandlungsort.BehandlungsorteSaveRequestEvent",
          processId: this.$route.params.process,
          taskId: this.$route.params.task,
          behandlungsort: behandlungsort,
        });
      }
    },
    remove: function (behandlungsort) {
      this.deleteItem = behandlungsort;
      this.deleteDialog = true;
    },
    deleteBehandlungsort: function () {
      this.$store.commit("startLoading");
      this.$socket.sendObj({
        eventID:
          "de.t2med.lizenzomat.eventapi.usecase.order.behandlungsort.BehandlungsortDeleteRequestEvent",
        behandlungsortId: this.deleteItem.id,
      });
      this.deleteItem = null;
      this.deleteDialog = false;
    },
    back: function () {
      this.$store.commit("startLoading");
      this.$socket.sendObj({
        eventID:
          "de.t2med.lizenzomat.eventapi.usecase.order.BackwardRequestEvent",
        taskId: this.$route.params.task,
        processId: this.$route.params.process,
      });
    },
    forward: function () {
      this.$store.commit("startLoading");
      this.$socket.sendObj({
        eventID:
          "de.t2med.lizenzomat.eventapi.usecase.order.behandlungsort.BehandlungsorteRequestEvent",
        taskId: this.$route.params.task,
        processId: this.$route.params.process,
      });
    },
  },
};
</script>
